import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Editor from '@monaco-editor/react';
import {
  ArrowLeft,
  Save,
  Trash2,
  FilePlus,
  ChevronDown,
  ChevronRight,
  Plus,
  Info,
  Code,
  Plug,
  KeyRound,
  Brain,
  History,
  Activity,
  Loader2,
  Play,
  Boxes,
  Rocket,
  X,
} from 'lucide-react';
import {
  getAgentSubagent,
  readAgentSubagentFile,
  writeAgentSubagentFile,
  deleteAgentSubagentFile,
  uninstallAgentSubagent,
  reloadService,
  listAdapters,
  getAgentSubagentCredentials,
  putAgentSubagentCredentials,
  getAgentSubagentParentMemory,
  setAgentSubagentRelayNumber,
} from '../data';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import SkillFileTree from '../components/SkillFileTree';
import JsonSchemaForm from '../components/JsonSchemaForm';
import './SkillWorkspace.css';
import './SubagentWorkspace.css';

const TABS = [
  { id: 'info', label: 'Info', icon: Info },
  { id: 'setup', label: 'Setup', icon: Rocket },
  { id: 'code', label: 'Code', icon: Code },
  { id: 'connections', label: 'Connections', icon: Plug },
  { id: 'credentials', label: 'Credentials', icon: KeyRound },
  { id: 'memory', label: 'Memory', icon: Brain },
  { id: 'runs', label: 'Runs', icon: History },
  { id: 'live', label: 'Live', icon: Activity },
];

const PROTECTED_FILES = new Set(['SUBAGENT.md', 'manifest.json']);

function languageForPath(path) {
  const lower = (path || '').toLowerCase();
  if (lower.endsWith('.py')) return 'python';
  if (lower.endsWith('.md')) return 'markdown';
  if (lower.endsWith('.json')) return 'json';
  if (lower.endsWith('.yaml') || lower.endsWith('.yml')) return 'yaml';
  if (lower.endsWith('.sh')) return 'shell';
  return 'plaintext';
}

function pickInitialFile(files) {
  if (!files || files.length === 0) return '';
  const main = files.find((f) => f === 'main.py');
  if (main) return main;
  const doc = files.find((f) => f === 'SUBAGENT.md');
  if (doc) return doc;
  const conn = files.find((f) => f === 'connections.json');
  if (conn) return conn;
  return files[0];
}

export function SubagentConfig({ agentId, subId, onSubLoaded, activeTab: externalTab }) {
  // When a host (the workflow-builder modal) drives the tab, we render only
  // the active panel and let the host supply the sidebar chrome — so the
  // modal is identical to skills/listeners. Uncontrolled = the standalone
  // page, which renders its own tab strip. The host's "settings" == "info".
  const controlled = externalTab != null;
  const [internalTab, setInternalTab] = useState('info');
  const activeTab = controlled
    ? (externalTab === 'settings' ? 'info' : externalTab)
    : internalTab;
  const setActiveTab = setInternalTab;
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingInfo, setSavingInfo] = useState(false);

  // Code tab state.
  const [activeFile, setActiveFile] = useState('');
  const [fileContent, setFileContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [fileDirty, setFileDirty] = useState(false);
  const [fileError, setFileError] = useState('');
  const [fileSaving, setFileSaving] = useState(false);
  const [showAddFile, setShowAddFile] = useState(false);
  const [newFileName, setNewFileName] = useState('');
  const debounceRef = useRef(null);

  const refreshSub = useCallback(async () => {
    const data = await getAgentSubagent(agentId, subId);
    setSub(data);
    return data;
  }, [agentId, subId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const data = await getAgentSubagent(agentId, subId);
        if (!cancelled) setSub(data);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load subagent');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [agentId, subId]);

  // Bubble the loaded record up so a host (page header or canvas node) can
  // reflect name/connection changes without re-fetching.
  useEffect(() => { if (sub && onSubLoaded) onSubLoaded(sub); }, [sub]); // eslint-disable-line react-hooks/exhaustive-deps

  // ----- Info tab save (debounced via the Save button — explicit, not auto) -

  const handleInfoSave = async () => {
    if (!sub) return;
    setSavingInfo(true);
    setError('');
    try {
      const raw = sub.raw || '';
      const { body } = parseFrontmatter(raw);
      // Base the save on the backend-parsed frontmatter — the client parser is
      // lossy on block scalars (`|`) and nested keys, so rebuilding from raw
      // would clobber system_prompt / goal_template / memory_scope.
      const fm = { ...(sub.frontmatter || {}) };
      const fields = ['name', 'description', 'version', 'goal_template', 'max_turns', 'timeout_seconds', 'system_prompt', 'disclosure'];
      for (const f of fields) {
        if (f === 'version' || f === 'max_turns' || f === 'timeout_seconds') {
          const n = Number(sub[f]);
          if (Number.isFinite(n)) fm[f] = n;
        } else {
          // Preserve the existing frontmatter value when this field wasn't
          // loaded/edited, so saving Info never blanks system_prompt/goal.
          fm[f] = sub[f] ?? fm[f] ?? '';
        }
      }
      const newRaw = renderFrontmatter(fm, body || '');
      await writeAgentSubagentFile(agentId, subId, 'SUBAGENT.md', newRaw);
      await refreshSub();
    } catch (e) {
      setError(e.message || 'Failed to save info');
    } finally {
      setSavingInfo(false);
    }
  };

  // ----- Code tab: file selection / load / save -----------------------------

  const loadFile = useCallback(async (path) => {
    if (!path) return;
    setFileError('');
    try {
      const data = await readAgentSubagentFile(agentId, subId, path);
      setActiveFile(path);
      setFileContent(data.content || '');
      setOriginalContent(data.content || '');
      setFileDirty(false);
    } catch (e) {
      setFileError(e.message || 'Failed to read file');
    }
  }, [agentId, subId]);

  useEffect(() => {
    if (sub && sub.files && sub.files.length > 0 && !activeFile) {
      loadFile(pickInitialFile(sub.files));
    }
  }, [sub, activeFile, loadFile]);

  const handleEditorChange = (value) => {
    setFileContent(value || '');
    setFileDirty((value || '') !== originalContent);
  };

  const handleSaveFile = async () => {
    if (!activeFile) return;
    setFileSaving(true);
    setFileError('');
    try {
      await writeAgentSubagentFile(agentId, subId, activeFile, fileContent);
      setOriginalContent(fileContent);
      setFileDirty(false);
      await refreshSub();
    } catch (e) {
      setFileError(e.message || 'Failed to save file');
    } finally {
      setFileSaving(false);
    }
  };

  const handleDeleteFile = async (path) => {
    if (PROTECTED_FILES.has(path)) {
      setFileError(`Cannot delete protected file: ${path}`);
      return;
    }
    if (!confirm(`Delete ${path}? This cannot be undone.`)) return;
    try {
      await deleteAgentSubagentFile(agentId, subId, path);
      if (activeFile === path) {
        setActiveFile('');
        setFileContent('');
        setOriginalContent('');
      }
      await refreshSub();
    } catch (e) {
      setFileError(e.message || 'Failed to delete file');
    }
  };

  const handleAddFile = async () => {
    const name = (newFileName || '').trim();
    if (!name || name.includes('/') || name.includes('..')) {
      setFileError('Invalid file name');
      return;
    }
    if (PROTECTED_FILES.has(name)) {
      setFileError(`${name} is reserved`);
      return;
    }
    try {
      await writeAgentSubagentFile(agentId, subId, name, '');
      setNewFileName('');
      setShowAddFile(false);
      await refreshSub();
      loadFile(name);
    } catch (e) {
      setFileError(e.message || 'Failed to create file');
    }
  };

  // Debounced reload-after-edit so a fresh save refetches the manifest.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {}, 0);
    return () => clearTimeout(debounceRef.current);
  }, []);

  if (loading) {
    return <div className="skill-ws-body"><div className="agent-loading"><div className="loading-spinner" /></div></div>;
  }
  if (!sub) {
    return <div className="skill-ws-body"><div className="agent-not-found">{error || 'Subagent not found'}</div></div>;
  }

  const tabStrip = (
    <div className="skill-ws-tabs">
      {TABS.filter((t) => t.id !== 'setup' || sub.relay_channel || sub.setup_guide).map((t) => {
        const Icon = t.icon;
        return (
          <button
            key={t.id}
            className={`skill-ws-tab ${activeTab === t.id ? 'active' : ''}`}
            onClick={() => setActiveTab(t.id)}
          >
            <Icon size={14} /> {t.label}
          </button>
        );
      })}
    </div>
  );

  const body = (
      <div className="skill-ws-body">
        {activeTab === 'setup' && (
          <SetupTab sub={sub} agentId={agentId} subId={subId} refreshSub={refreshSub} />
        )}

        {activeTab === 'info' && (
          <InfoTab
            sub={sub}
            setSub={setSub}
            saving={savingInfo}
            onSave={handleInfoSave}
          />
        )}

        {activeTab === 'code' && (
          <CodeTab
            sub={sub}
            activeFile={activeFile}
            fileContent={fileContent}
            fileDirty={fileDirty}
            fileSaving={fileSaving}
            fileError={fileError}
            onLoadFile={loadFile}
            onEditorChange={handleEditorChange}
            onSave={handleSaveFile}
            onDeleteFile={handleDeleteFile}
            showAddFile={showAddFile}
            setShowAddFile={setShowAddFile}
            newFileName={newFileName}
            setNewFileName={setNewFileName}
            onAddFile={handleAddFile}
          />
        )}

        {activeTab === 'connections' && <ConnectionsTab
          sub={sub}
          setSub={setSub}
          agentId={agentId}
          subId={subId}
          refreshSub={refreshSub}
        />}

        {activeTab === 'credentials' && <CredentialsTab
          agentId={agentId}
          subId={subId}
          onChange={refreshSub}
        />}

        {activeTab === 'memory' && <MemoryTab
          agentId={agentId}
          subId={subId}
          sub={sub}
        />}

        {activeTab === 'runs' && <RunsTab />}

        {activeTab === 'live' && <LiveTab />}
      </div>
  );

  // Host-driven (modal): only the active panel, host owns the sidebar chrome.
  if (controlled) {
    return (
      <>
        {error && <div className="skill-error-banner">{error}</div>}
        {body}
      </>
    );
  }

  // Standalone page: our own tab strip alongside the body.
  return (
    <>
      {error && <div className="skill-error-banner">{error}</div>}
      <div className="sa-cfg-cols">
        {tabStrip}
        {body}
      </div>
    </>
  );
}


// Page wrapper: header chrome + the shared config body. The config is also
// embedded, chrome-free, inside the workflow builder's node popup.
export default function SubagentWorkspace() {
  const { agentId, subId } = useParams();
  const navigate = useNavigate();
  const [headerSub, setHeaderSub] = useState(null);

  const handleUninstall = async () => {
    if (!confirm(`Uninstall subagent "${headerSub?.name || subId}"?`)) return;
    await uninstallAgentSubagent(agentId, subId);
    reloadService().catch(() => {});
    navigate(`/portal/agent/${agentId}/subagents`);
  };

  return (
    <div className="skill-workspace">
      <div className="skill-ws-header">
        <button className="skill-ws-back" onClick={() => navigate(`/portal/agent/${agentId}/subagents`)}>
          <ArrowLeft size={14} /> Subagents
        </button>
        <div className="skill-ws-title">
          <span className="skill-ws-icon"><Boxes size={20} /></span>
          <div>
            <h2>{headerSub?.name || headerSub?.slug || subId}</h2>
            <span className="skill-ws-subtitle">
              {headerSub?.connections_count || 0} connection{(headerSub?.connections_count === 1) ? '' : 's'}
            </span>
          </div>
        </div>
        <div className="skill-ws-actions">
          <button className="lw-btn lw-btn-ghost" title="Subagent runner ships in a later slice" disabled>
            <Play size={14} /> Run
          </button>
          <button className="lw-btn lw-btn-ghost lw-btn-danger" onClick={handleUninstall}>
            <Trash2 size={14} /> Uninstall
          </button>
        </div>
      </div>
      <SubagentConfig agentId={agentId} subId={subId} onSubLoaded={setHeaderSub} />
    </div>
  );
}


// ----- Info tab ------------------------------------------------------------

function InfoTab({ sub, setSub, saving, onSave }) {
  const update = (key, value) => setSub({ ...sub, [key]: value });
  return (
    <div className="skill-info">
      <div className="skill-info-grid">
        <Field label="Name">
          <input
            type="text"
            value={sub.name || ''}
            onChange={(e) => update('name', e.target.value)}
            className="form-input"
          />
        </Field>
        <Field label="Description">
          <input
            type="text"
            value={sub.description || ''}
            onChange={(e) => update('description', e.target.value)}
            className="form-input"
          />
        </Field>
        <Field label="Version">
          <input
            type="number"
            value={sub.version || 1}
            onChange={(e) => update('version', Number(e.target.value))}
            className="form-input"
            min={1}
          />
        </Field>
        <Field label="Max turns">
          <input
            type="number"
            value={sub.max_turns || 25}
            onChange={(e) => update('max_turns', Number(e.target.value))}
            className="form-input"
            min={1}
            max={200}
          />
        </Field>
        <Field label="Timeout (s)">
          <input
            type="number"
            value={sub.timeout_seconds || 300}
            onChange={(e) => update('timeout_seconds', Number(e.target.value))}
            className="form-input"
            min={10}
            max={3600}
          />
        </Field>
      </div>
      <Field label="System prompt">
        <textarea
          rows={7}
          value={sub.system_prompt ?? sub.frontmatter?.system_prompt ?? ''}
          onChange={(e) => update('system_prompt', e.target.value)}
          className="form-input"
          placeholder="The persona / standing instructions used on every run (e.g. a warm, concise phone assistant)."
        />
      </Field>
      <Field label="Disclosure">
        <textarea
          rows={2}
          value={sub.disclosure ?? sub.frontmatter?.disclosure ?? ''}
          onChange={(e) => update('disclosure', e.target.value)}
          className="form-input"
          placeholder="Spoken at the start of a call, e.g. “This call is handled by an AI assistant and may be recorded.” Leave blank for none."
        />
      </Field>
      <Field label="Goal template">
        <textarea
          rows={6}
          value={sub.frontmatter?.goal_template || ''}
          onChange={(e) => update('goal_template', e.target.value)}
          className="form-input"
          placeholder="Complete: {{parent.payload.user_request}}"
        />
      </Field>
      <div className="skill-info-actions">
        <button className="btn btn-primary" onClick={onSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save Info'}
        </button>
      </div>
    </div>
  );
}


// ----- Setup tab -----------------------------------------------------------

function SetupTab({ sub, agentId, subId, refreshSub }) {
  const [number, setNumber] = useState(sub.relay_number || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const hookUrl = sub.relay_hook_url || '';

  const saveNumber = async () => {
    setSaving(true);
    setError('');
    try {
      await setAgentSubagentRelayNumber(agentId, subId, number.trim());
      await refreshSub();
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    } catch (e) {
      setError(e.message || 'Failed to save number');
    } finally {
      setSaving(false);
    }
  };

  const copyUrl = () => {
    if (!hookUrl) return;
    navigator.clipboard?.writeText(hookUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="skill-setup">
      {sub.relay_channel && (
        <div className="skill-setup-panel">
          <h3>Connect your phone number</h3>
          <Field label="Webhook URL">
            <div className="skill-setup-copy">
              <input
                className="form-input"
                readOnly
                value={hookUrl || '(relay URL not configured on the server)'}
                onFocus={(e) => e.target.select()}
              />
              <button className="btn btn-primary btn-sm" onClick={copyUrl} disabled={!hookUrl}>
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          </Field>
          <Field label="Your Twilio number">
            <input
              className="form-input"
              placeholder="+14155551234"
              value={number}
              onChange={(e) => setNumber(e.target.value)}
            />
          </Field>
          <div className="skill-setup-actions">
            <button className="btn btn-primary" onClick={saveNumber} disabled={saving}>
              {saving ? 'Saving…' : 'Save number'}{saved ? ' ✓' : ''}
            </button>
          </div>
          {error && <div className="skill-error-banner">{error}</div>}
        </div>
      )}
      {sub.setup_guide && (
        <div className="skill-setup-guide">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{sub.setup_guide}</ReactMarkdown>
        </div>
      )}
      {!sub.relay_channel && !sub.setup_guide && (
        <div className="skill-empty-state">No setup needed for this subagent.</div>
      )}
    </div>
  );
}


// ----- Code tab ------------------------------------------------------------

function CodeTab({
  sub, activeFile, fileContent, fileDirty, fileSaving, fileError,
  onLoadFile, onEditorChange, onSave, onDeleteFile,
  showAddFile, setShowAddFile, newFileName, setNewFileName, onAddFile,
}) {
  const files = sub?.files || [];
  return (
    <div className="skill-code">
      <div className="skill-code-layout">
        <div className="skill-code-tree">
          <div className="skill-code-tree-header">
            <span>Files</span>
            <button
              className="lw-btn lw-btn-ghost lw-btn-sm"
              onClick={() => setShowAddFile(true)}
              title="Add file"
            >
              <FilePlus size={14} />
            </button>
          </div>
          {showAddFile && (
            <div className="skill-code-addfile">
              <input
                type="text"
                value={newFileName}
                onChange={(e) => setNewFileName(e.target.value)}
                placeholder="notes.md"
                className="form-input"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onAddFile();
                  if (e.key === 'Escape') setShowAddFile(false);
                }}
              />
              <button className="lw-btn lw-btn-primary lw-btn-sm" onClick={onAddFile}>
                <Plus size={12} />
              </button>
              <button
                className="lw-btn lw-btn-ghost lw-btn-sm"
                onClick={() => setShowAddFile(false)}
              >
                <X size={12} />
              </button>
            </div>
          )}
          <SkillFileTree
            files={files}
            activePath={activeFile}
            onPick={onLoadFile}
          />
        </div>

        <div className="skill-code-editor">
          <div className="skill-code-toolbar">
            <span className="skill-code-current-file">{activeFile || '(no file selected)'}</span>
            <div className="skill-code-toolbar-actions">
              {fileDirty && <span className="skill-dirty-indicator">● unsaved</span>}
              <button
                className="btn btn-primary btn-sm"
                onClick={onSave}
                disabled={!activeFile || !fileDirty || fileSaving}
              >
                {fileSaving ? 'Saving…' : <><Save size={14} /> Save</>}
              </button>
            </div>
          </div>
          {fileError && <div className="skill-error-banner">{fileError}</div>}
          <div className="skill-code-monaco">
            {activeFile ? (
              <Editor
                height="100%"
                language={languageForPath(activeFile)}
                value={fileContent}
                onChange={onEditorChange}
                options={{
                  minimap: { enabled: false },
                  fontSize: 13,
                  wordWrap: 'on',
                  automaticLayout: true,
                  tabSize: 2,
                }}
                theme="vs-dark"
              />
            ) : (
              <div className="skill-code-empty">
                Select a file on the left, or add a new one with the + button.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


// ----- Placeholder tab -----------------------------------------------------

function PlaceholderTab({ title, icon: Icon, message }) {
  return (
    <div className="skill-placeholder-tab">
      <div className="skill-placeholder-icon"><Icon size={32} /></div>
      <h3>{title}</h3>
      <p>{message}</p>
    </div>
  );
}


// ----- Connections tab -----------------------------------------------------

function ConnectionsTab({ sub, setSub, agentId, subId, refreshSub }) {
  const [adapters, setAdapters] = useState([]);
  const [adaptersErr, setAdaptersErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [vault, setVault] = useState({});

  // Load adapters once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await listAdapters();
        if (!cancelled) setAdapters(list);
      } catch (e) {
        if (!cancelled) setAdaptersErr(e.message || 'Failed to load adapters');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Load declared credential keys for the credential widget (Chunk 7 will
  // add real saved-value resolution; for now we send only declared keys).
  useEffect(() => {
    // The detail endpoint already returns ``frontmatter``. Any
    // ``format: credential`` field's value is treated as a vault key.
    // We surface them so the user sees which keys exist.
    const fm = sub?.frontmatter || {};
    const declared = Array.isArray(fm.inherit_credentials) ? fm.inherit_credentials : [];
    const map = {};
    for (const k of declared) map[k] = '••••';
    setVault(map);
  }, [sub?.slug, sub?.frontmatter]);

  const connections = Array.isArray(sub?.frontmatter?.connections)
    ? sub.frontmatter.connections
    : [];

  const grouped = useMemo(() => {
    const buckets = { bundled: [], community: [], per_block: [] };
    for (const a of adapters) {
      (buckets[a.source] || (buckets[a.source] = [])).push(a);
    }
    return buckets;
  }, [adapters]);

  const updateConnections = (next) => {
    setSub({
      ...sub,
      frontmatter: { ...(sub.frontmatter || {}), connections: next },
    });
  };

  const updateConnection = (idx, patch) => {
    const next = connections.map((c, i) => (i === idx ? { ...c, ...patch } : c));
    updateConnections(next);
  };

  const addConnection = () => {
    const first = adapters.find((a) => a.directions?.includes?.('outbound')) || adapters[0];
    if (!first) return;
    updateConnections([
      ...connections,
      {
        name: `conn_${connections.length + 1}`,
        adapter: first.name,
        direction: 'outbound',
        config: defaultConfigFor(first),
      },
    ]);
  };

  const removeConnection = (idx) => {
    updateConnections(connections.filter((_, i) => i !== idx));
  };

  const adapterFor = (name) => adapters.find((a) => a.name === name);

  const saveConnections = async () => {
    setSaving(true);
    setError('');
    try {
      const raw = sub.raw || '';
      const { frontmatter, body } = parseFrontmatter(raw);
      const fm = { ...(frontmatter || {}), connections };
      const newRaw = renderFrontmatter(fm, body || '');
      await writeAgentSubagentFile(agentId, subId, 'SUBAGENT.md', newRaw);
      await refreshSub();
    } catch (e) {
      setError(e.message || 'Failed to save connections');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="skill-connections">
      <div className="skill-connections-header">
        <div>
          <h3>Connections</h3>
          <p>
            Each row binds an installed adapter to its config. Pick an adapter,
            fill in the form (auto-generated from its schema), and save.
          </p>
        </div>
        <div className="skill-connections-actions">
          <button className="btn btn-primary btn-sm" onClick={addConnection} disabled={!adapters.length}>
            <Plus size={14} /> Add connection
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={saveConnections}
            disabled={saving || !sub?.frontmatter}
          >
            {saving ? 'Saving…' : 'Save connections'}
          </button>
        </div>
      </div>

      {adaptersErr && <div className="skill-error-banner">{adaptersErr}</div>}
      {error && <div className="skill-error-banner">{error}</div>}

      {connections.length === 0 ? (
        <div className="skill-empty-state">
          No connections yet. Click <strong>Add connection</strong> to pick an
          adapter (bundled or community).
        </div>
      ) : (
        <div className="skill-connections-list">
          {connections.map((conn, idx) => {
            const adapter = adapterFor(conn.adapter);
            return (
              <ConnectionRow
                key={idx}
                conn={conn}
                adapter={adapter}
                adapters={adapters}
                grouped={grouped}
                onChange={(patch) => updateConnection(idx, patch)}
                onRemove={() => removeConnection(idx)}
                vault={vault}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}


function ConnectionRow({ conn, adapter, adapters, grouped, onChange, onRemove, vault }) {
  return (
    <div className="skill-connection-row">
      <div className="skill-connection-row-header">
        <input
          type="text"
          value={conn.name || ''}
          onChange={(e) => onChange({ name: e.target.value })}
          className="form-input"
          placeholder="connection name"
        />
        <select
          className="form-input"
          value={conn.adapter || ''}
          onChange={(e) => {
            const next = adapters.find((a) => a.name === e.target.value);
            onChange({
              adapter: e.target.value,
              direction: next?.directions?.includes?.(conn.direction)
                ? conn.direction
                : (next?.directions?.[0] || 'outbound'),
              config: defaultConfigFor(next),
            });
          }}
        >
          {Object.entries(grouped).map(([src, items]) =>
            items.length === 0 ? null : (
              <optgroup key={src} label={src.replace('_', ' ')}>
                {items.map((a) => (
                  <option key={a.name} value={a.name}>
                    {a.display_name} ({a.name})
                  </option>
                ))}
              </optgroup>
            )
          )}
        </select>
        <select
          className="form-input"
          value={conn.direction || 'outbound'}
          onChange={(e) => onChange({ direction: e.target.value })}
        >
          {(adapter?.directions || ['outbound']).map((d) => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>
        <button
          className="lw-btn lw-btn-ghost lw-btn-sm lw-btn-danger"
          onClick={onRemove}
          title="Remove connection"
        >
          <Trash2 size={14} />
        </button>
      </div>

      <div className="skill-connection-row-meta">
        {adapter ? (
          <>
            <span className="skill-connection-kind">{adapter.kind}</span>
            <span className="skill-connection-caps">
              capabilities: {(adapter.capabilities || []).join(', ') || '—'}
            </span>
            <button
              className="lw-btn lw-btn-ghost lw-btn-sm"
              disabled
              title="Test connection lands with the runner"
            >
              Test connection
            </button>
          </>
        ) : (
          <span className="skill-connection-warning">
            Adapter <code>{conn.adapter}</code> is not installed — install it or
            pick a different adapter.
          </span>
        )}
      </div>

      {adapter && (
        <div className="skill-connection-form">
          <JsonSchemaForm
            schema={adapter.config_schema || { type: 'object', properties: {} }}
            value={conn.config || {}}
            onChange={(v) => onChange({ config: v })}
            vault={vault}
          />
        </div>
      )}
    </div>
  );
}


function defaultConfigFor(adapter) {
  // Seed new connections with required-field defaults so the form is
  // valid (or at least minimal) the moment the row appears.
  if (!adapter?.config_schema) return {};
  const required = new Set(adapter.config_schema.required || []);
  const out = {};
  for (const [key, prop] of Object.entries(adapter.config_schema.properties || {})) {
    if (required.has(key) || prop.default !== undefined) {
      out[key] = prop.default !== undefined ? prop.default : '';
    }
  }
  return out;
}


// ----- Credentials tab -----------------------------------------------------

const SENSITIVE_KEY_RE = /secret|token|password|key|credential/i;

function MemoryTab({ agentId, subId, sub }) {
  const [parent, setParent] = useState(null);
  const [scratch, setScratch] = useState('');
  const [scratchSaving, setScratchSaving] = useState(false);
  const [scratchDirty, setScratchDirty] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await getAgentSubagentParentMemory(agentId, subId);
        if (!cancelled) setParent(data);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load parent memory');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [agentId, subId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await readAgentSubagentFile(agentId, subId, 'scratch.md');
        if (!cancelled) {
          setScratch(data?.content || '');
          setScratchDirty(false);
        }
      } catch {
        // scratch.md is optional — start blank.
        if (!cancelled) {
          setScratch('');
          setScratchDirty(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [agentId, subId]);

  const saveScratch = async () => {
    setScratchSaving(true);
    setError('');
    try {
      await writeAgentSubagentFile(agentId, subId, 'scratch.md', scratch);
      setScratchDirty(false);
    } catch (e) {
      setError(e.message || 'Failed to save scratch');
    } finally {
      setScratchSaving(false);
    }
  };

  const summaries = parent?.thread_summaries || [];
  const readKeys = parent?.read_keys || [];

  return (
    <div className="skill-memory">
      <div className="skill-memory-grid">
        <div className="skill-memory-panel">
          <h3>Parent memory (read-only)</h3>
          <p className="skill-memory-helper">
            What the subagent sees from the parent thread at spawn time.
            Declared read scope: <code>{(readKeys.join(', ') || '—')}</code>
          </p>
          {loading ? (
            <div className="skills-loading" style={{ padding: 16 }}>Loading…</div>
          ) : summaries.length === 0 ? (
            <div className="skill-memory-empty">
              No parent thread summaries yet.
            </div>
          ) : (
            <ul className="skill-memory-summaries">
              {summaries.map((s) => (
                <li key={s.thread_id}>
                  <div className="skill-memory-thread-id">
                    {s.thread_id}
                    {s.updated_at && (
                      <span className="skill-memory-ts">
                        {new Date(s.updated_at).toLocaleString()}
                      </span>
                    )}
                  </div>
                  <div className="skill-memory-summary">{s.summary}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="skill-memory-panel">
          <h3>Private scratch</h3>
          <p className="skill-memory-helper">
            The subagent writes here during its run. 32 KB cap.
            <button
              className="btn btn-sm btn-primary"
              style={{ marginLeft: 8 }}
              onClick={saveScratch}
              disabled={!scratchDirty || scratchSaving}
            >
              {scratchSaving ? 'Saving…' : 'Save'}
            </button>
            {scratchDirty && <span className="skill-dirty-indicator" style={{ marginLeft: 8 }}>● unsaved</span>}
          </p>
          <textarea
            className="form-input"
            rows={14}
            value={scratch}
            onChange={(e) => {
              setScratch(e.target.value);
              setScratchDirty(true);
            }}
            placeholder="Private scratch space — the subagent writes here during its run."
          />
          {error && <div className="skill-error-banner">{error}</div>}
        </div>
      </div>
    </div>
  );
}


function RunsTab() {
  return (
    <div className="skill-runs">
      <div className="skill-runs-header">
        <h3>Runs</h3>
        <p>No runs yet. The table below shows the columns that land once the runner ships.</p>
      </div>
      <table className="skill-runs-table">
        <thead>
          <tr>
            <th>rid</th>
            <th>status</th>
            <th>duration</th>
            <th>cost</th>
            <th>turns</th>
            <th>Replay</th>
            <th>Logs</th>
          </tr>
        </thead>
        <tbody>
          <tr className="skill-runs-empty">
            <td colSpan={7}>(empty)</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}


function LiveTab() {
  return (
    <div className="skill-placeholder-tab">
      <div className="skill-placeholder-icon"><Activity size={32} /></div>
      <h3>Live</h3>
      <p>Live view is visible only when a run is active. The runner ships in a later slice.</p>
    </div>
  );
}


function CredentialsTab({ agentId, subId, onChange }) {
  const [meta, setMeta] = useState({ keys: [], credentials: {}, inherit: [], declared: [] });
  const [values, setValues] = useState({});
  const [inherit, setInherit] = useState([]);
  const [revealed, setRevealed] = useState({});
  const [removed, setRemoved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');

  const refresh = useCallback(async () => {
    const data = await getAgentSubagentCredentials(agentId, subId);
    setMeta(data || { keys: [], credentials: {}, inherit: [], declared: [] });
    setInherit(Array.isArray(data?.inherit) ? data.inherit : []);
    // Pre-populate edit buffer with declared keys (declared + already-stored).
    const next = {};
    const allKeys = new Set([
      ...(data?.fields || []).map((f) => f.name),
      ...(data?.declared || []),
      ...(data?.keys || []),
    ]);
    for (const k of allKeys) next[k] = '';
    setValues(next);
    setRevealed({});
    setRemoved([]);
  }, [agentId, subId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        await refresh();
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load credentials');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [refresh]);

  // Rich field schema (from the catalog bundle) keyed by name, so we can
  // render labeled/typed inputs like a catalog skill's credentials.
  const fieldMap = useMemo(() => {
    const m = {};
    for (const f of meta.fields || []) if (f && f.name) m[f.name] = f;
    return m;
  }, [meta.fields]);

  const allKeys = useMemo(() => {
    const set = new Set([
      ...(meta.fields || []).map((f) => f.name),
      ...(meta.declared || []),
      ...(meta.keys || []),
      ...Object.keys(values),
    ]);
    return Array.from(set);
  }, [meta, values]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {};
      Object.entries(values).forEach(([k, v]) => {
        if (v === '' || v == null) return;
        // Skip inherited keys — they're owned by the parent.
        if (inherit.includes(k)) return;
        payload[k] = v;
      });
      const toRemove = removed.filter((k) => !(k in payload));
      await putAgentSubagentCredentials(agentId, subId, {
        credentials: payload,
        remove: toRemove,
        inherit,
      });
      await refresh();
      onChange?.();
    } catch (e) {
      setError(e.message || 'Failed to save credentials');
    } finally {
      setSaving(false);
    }
  };

  const handleAddPair = () => {
    const k = newKey.trim();
    if (!k) return;
    setValues((prev) => ({ ...prev, [k]: newValue }));
    setNewKey('');
    setNewValue('');
  };

  const toggleInherit = (k) => {
    setInherit((prev) => {
      if (prev.includes(k)) return prev.filter((x) => x !== k);
      // If we mark it inherited, drop any pending value (the parent owns it).
      setValues((vp) => ({ ...vp, [k]: '' }));
      return [...prev, k];
    });
  };

  const handleRemove = (k) => {
    if (!window.confirm(`Remove credential "${k}"?`)) return;
    setValues((prev) => {
      const next = { ...prev };
      delete next[k];
      return next;
    });
    setInherit((prev) => prev.filter((x) => x !== k));
    if ((meta.keys || []).includes(k)) {
      setRemoved((prev) => (prev.includes(k) ? prev : [...prev, k]));
    }
  };

  return (
    <div className="skill-creds">
      <div className="skill-creds-header">
        <h3>Credentials</h3>
        <p>
          Values are passed to the subagent as environment variables when its
          connections open. Toggle <strong>Inherit</strong> to defer a key to
          the parent agent's vault — the value never lands in this subagent.
          Existing values are masked; type a new value to overwrite.
        </p>
      </div>

      {error && <div className="skill-error-banner">{error}</div>}

      {loading ? (
        <div className="skills-loading" style={{ padding: 32 }}>Loading credentials…</div>
      ) : (
        <>
          {allKeys.length === 0 && Object.keys(values).length === 0 ? (
            <div className="skill-empty-creds">
              No credentials declared yet. Add one below, or add a connection
              with a <code>format: credential</code> field on the Connections
              tab.
            </div>
          ) : (
            <div className="skill-creds-list">
              {allKeys.map((k) => {
                const field = fieldMap[k] || {};
                const sensitive = field.type === 'password' || SENSITIVE_KEY_RE.test(k);
                const masked = (meta.credentials || {})[k];
                const isInherited = inherit.includes(k);
                const inputValue = values[k] !== undefined ? values[k] : '';
                const isRevealed = !!revealed[k];
                return (
                  <div className="skill-cred-row" key={k}>
                    <label htmlFor={`sub-cred-${k}`}>{field.label || k}</label>
                    <div className="skill-cred-input-row">
                      {isInherited ? (
                        <span className="skill-cred-inherited">
                          (inherited from parent agent)
                        </span>
                      ) : (
                        <input
                          id={`sub-cred-${k}`}
                          className="skill-cred-input"
                          type={sensitive && !isRevealed ? 'password' : 'text'}
                          autoComplete="off"
                          placeholder={masked ? `Saved: ${masked}` : (field.placeholder || 'Not set')}
                          value={inputValue}
                          onChange={(e) =>
                            setValues((prev) => ({ ...prev, [k]: e.target.value }))
                          }
                        />
                      )}
                      {sensitive && !isInherited && (
                        <button
                          type="button"
                          className="lw-btn lw-btn-ghost"
                          onClick={() => setRevealed((p) => ({ ...p, [k]: !p[k] }))}
                          style={{ padding: '6px 10px' }}
                          title={isRevealed ? 'Hide' : 'Show'}
                        >
                          {isRevealed ? '🙈' : '👁'}
                        </button>
                      )}
                      <label className="skill-cred-inherit-toggle">
                        <input
                          type="checkbox"
                          checked={isInherited}
                          onChange={() => toggleInherit(k)}
                        />
                        Inherit
                      </label>
                      <button
                        type="button"
                        className="lw-btn lw-btn-danger"
                        style={{ padding: '6px 10px' }}
                        title="Remove"
                        onClick={() => handleRemove(k)}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="skill-cred-add">
            <input
              className="skill-cred-input"
              type="text"
              placeholder="NEW_KEY"
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              spellCheck={false}
              autoComplete="off"
            />
            <input
              className="skill-cred-input"
              type="text"
              placeholder="value"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              autoComplete="off"
            />
            <button className="btn btn-primary btn-sm" onClick={handleAddPair} disabled={!newKey.trim()}>
              <Plus size={14} /> Add
            </button>
          </div>

          <div className="skill-cred-save-row">
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save credentials'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}


// ----- Field + frontmatter helpers (frontend mirror of subagent_storage) --

function Field({ label, children }) {
  return (
    <label className="form-field">
      <span className="form-field-label">{label}</span>
      {children}
    </label>
  );
}

function parseFrontmatter(text) {
  if (!text || !text.startsWith('---')) return { frontmatter: {}, body: text };
  const end = text.indexOf('\n---', 3);
  if (end === -1) return { frontmatter: {}, body: text };
  const fmText = text.slice(3, end);
  const body = text.slice(end + 4).replace(/^\n+/, '');
  const frontmatter = {};
  for (const line of fmText.split('\n')) {
    if (!line.trim() || line.trim().startsWith('#')) continue;
    const idx = line.indexOf(':');
    if (idx === -1) continue;
    const key = line.slice(0, idx).trim();
    let value = line.slice(idx + 1).trim();
    // Strip surrounding quotes.
    if ((value.startsWith('"') && value.endsWith('"')) ||
        (value.startsWith("'") && value.endsWith("'"))) {
      value = value.slice(1, -1);
    }
    // Numbers / booleans.
    if (/^-?\d+$/.test(value)) frontmatter[key] = parseInt(value, 10);
    else if (/^-?\d+\.\d+$/.test(value)) frontmatter[key] = parseFloat(value);
    else if (value === 'true') frontmatter[key] = true;
    else if (value === 'false') frontmatter[key] = false;
    else frontmatter[key] = value;
  }
  return { frontmatter, body };
}

function renderFrontmatter(fm, body) {
  const lines = ['---'];
  for (const [k, v] of Object.entries(fm)) {
    if (typeof v === 'string' && v.includes('\n')) {
      lines.push(`${k}: |`);
      for (const line of v.split('\n')) lines.push(`  ${line}`);
    } else if (typeof v === 'string') {
      lines.push(`${k}: ${needsQuote(v) ? JSON.stringify(v) : v}`);
    } else if (typeof v === 'number' || typeof v === 'boolean') {
      lines.push(`${k}: ${v}`);
    } else {
      lines.push(`${k}: ${JSON.stringify(v)}`);
    }
  }
  lines.push('---');
  return lines.join('\n') + (body ? `\n\n${body}\n` : '\n');
}

function needsQuote(s) {
  if (s === '') return true;
  if (/^(true|false|null|yes|no)$/i.test(s)) return true;
  if (/^-?\d/.test(s)) return true;
  return /[:#\n\t]/.test(s) || /^\s|\s$/.test(s);
}
